import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { additionalFieldsData } from "variables/additional-fields-data";
import { containsNonLatinLetters } from "../../../utils/helper-functions/sanitize-non-latin-characters";

export const useAdditionalFieldValue = ({
  input,
  fields,
  template = {},
  isPixV2,
}) => {
  const { name, default_value = "", is_mutable, type, is_readonly } = input;

  const isEditable =
    input?.is_editable !== undefined && input.is_editable === 1;
  const isNotEditable =
    input?.is_editable !== undefined && input.is_editable === 0;

  const [inputValue, setInputValue] = useState("");
  const [readOnly, setReadOnly] = useState(false);
  const [isFirst, setIsFirst] = useState(true);

  const payData = useSelector((state) => state.payData);
  const payInfo = useSelector((state) => state.payInfo);
  const isTemplate = payData?.template_token;

  const getInitialPixV2FieldValue = () => {
    let savedValue = default_value || template[name] || "";
    const cachedLength = Object.keys(
      payInfo?.inputEditorCachedValue || {},
    ).length;

    if (!savedValue && default_value && cachedLength) {
      savedValue = default_value;
    }

    const isMasked = savedValue?.includes("*");
    const isPreviouslySavedValue =
      savedValue?.length > 0 && fields[name] === savedValue;
    const { is_account_identifier } = input;

    const isSensitiveData =
      type === "email" ||
      type === "phone" ||
      type === "cpf" ||
      type === "cnpj" ||
      type === "cpf_cnpj";

    if (isEditable || isNotEditable) {
      if (input?.is_masked !== undefined && input.is_masked === 0) {
        return fields[name];
      }
    }

    return isPreviouslySavedValue &&
      !isMasked &&
      isSensitiveData &&
      is_account_identifier === 0
      ? additionalFieldsData[type]?.maskFunction(savedValue)
      : fields[name];
  };

  useEffect(() => {
    const valueToSet = isPixV2
      ? getInitialPixV2FieldValue()
      : (fields[name] ?? template[name] ?? default_value);

    const hasInitialValue =
      (template[name] && template[name].length > 0) ||
      (default_value && default_value?.length > 0);

    const containsNonLatin = containsNonLatinLetters(valueToSet);
    const shouldBeReadOnly =
      is_readonly === 1 && default_value && default_value?.length > 0;

    if (containsNonLatin) {
      console.warn(`Initial value for ${name} contains non-Latin letters.`);
      setInputValue("");
      setReadOnly(false);
    } else {
      if (shouldBeReadOnly) {
        setReadOnly(true);
        setInputValue(isPixV2 ? getInitialPixV2FieldValue() : default_value);
      } else {
        if (isEditable || isNotEditable) {
          if (isEditable) {
            setInputValue(valueToSet);
            setReadOnly(false);
            return;
          }

          if (isNotEditable) {
            if (isTemplate) setInputValue(valueToSet);
            else setInputValue(fields[name]);
          }
        }

        switch (is_mutable) {
          case "keep_editable_if_empty":
            setInputValue(valueToSet);
            setReadOnly(hasInitialValue);
            break;
          case "keep_editable":
            setReadOnly(false);
            setInputValue(valueToSet);
            break;
          case "keep_empty":
            setReadOnly(false);
            if (isFirst) {
              setInputValue(
                isPixV2 ? getInitialPixV2FieldValue() : default_value,
              );
              setIsFirst(false);
            } else {
              setInputValue(valueToSet);
            }
            break;
          default:
            setReadOnly(false);
            setInputValue(valueToSet);
            break;
        }
      }
    }
  }, [default_value, fields, name, template, is_mutable, is_readonly, isFirst]);

  return { inputValue, setInputValue, readOnly };
};
